import { ArcElement, Chart as ChartJS, ChartOptions, Legend, Tooltip } from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useGetBrowserLanguageChartQuery } from '../../../../app/services/chatbot-rtk.services';
import LoadingComponent from '../../../common/loading/loading.component';
// Register required elements and components for Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

interface ChartProps {
  from: string;
  to: string;
  botId: string;
}

const BrowserLanguageChart: React.FC<ChartProps> = ({ from, to, botId }) => {
  const { t, i18n } = useTranslation();
  const { data, error, isLoading, isFetching } = useGetBrowserLanguageChartQuery({
    from,
    to,
    bot_id: botId,
  });
  const [chartData, setChartData] = useState<any>(null);
  const [count, setCount] = useState<number>(0);

  // Explicitly type the options
  const options: ChartOptions<'doughnut'> = {
    responsive: false, // Disable responsiveness
    plugins: {
      legend: {
        position: 'bottom', // Use allowed position values
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
          },
        },
      },
    },
    cutout: '70%', // Hollow center
  };

  useEffect(() => {
    setCount(0);
    setChartData(null);
    if (data) {
      setCount(data.map((dr: any) => dr.category).length);
      setChartData({
        labels: data.map((dr: any) => dr.category),
        datasets: [
          {
            label: 'Browser Languages',
            data: data.map((dr: any) => +dr.percentage),
            backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
          },
        ],
      });
    }
  }, [data, isFetching]);

  if (isLoading) return <LoadingComponent />;
  if (error) return <LoadingComponent />;
  return (
    <div className="tw-flex tw-flex-col tw-justify-center tw-items-center">
      <div className="tw-text-gray-800 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-uppercase">{t('Charts/BrowserLanguage/title')}</div>
      {count !== 0 ? (
        <div className="tw-text-gray-800 tw-text-3xl tw-font-medium tw-font-['Lexend']">{count}</div>
      ) : (
        <div className="tw-text-gray-400 tw-text-xl tw-font-medium tw-font-['Lexend']">{t('Charts/NoData')}</div>
      )}
      <div className="tw-text-gray-800 tw-text-base tw-font-normal tw-font-['Lexend']">{`${count !== 0 ? t('Charts/BrowserLanguage/description') : ''}`}</div>
      <div>
        {!chartData && <LoadingComponent />}
        {chartData && count !== 0 && (
          <div className="tw-max-h-[300px] tw-w-full tw-mt-6">
            <Doughnut data={chartData} options={options} height={300} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowserLanguageChart;
